"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlobalPointerDownOutsideListener = void 0;
/**
 * A singleton class used to trigger a handler when a pointer-down event occurs outside of a set of subscribing element(s).
 * When the user pointer-downs outside of the subscribed elements, their handlers are called.
 * Pointer-down events on iframes require special care, so this class exists to abstract those details.
 *
 * Maps 1 handler to many elements, e.g. for common use-case: clicking outside of a dropdown or its popup, causes the dropdown to close.
 * In this case the dropdown element and the popup are DOM-distant, so both elements are associated with the handler.
 */
var GlobalPointerDownOutsideListener = /** @class */ (function () {
    function GlobalPointerDownOutsideListener() {
    }
    /**
     * Subscribes the passed in element to a given handler, for any mousedowns/touchstarts that occur
     * outside of the handler's subscribing element(s).
     *
     * @returns a function which unsubscribes the passed in element from the passed in handler.
     */
    GlobalPointerDownOutsideListener.subscribe = function (element, handler) {
        var _this = this;
        var currentSubscriptions = this.subscriptionMap.get(handler) || [];
        if (currentSubscriptions.indexOf(element) === -1) {
            currentSubscriptions.push(element);
            this.subscriptionMap.set(handler, currentSubscriptions);
        }
        this.addOrRemoveEventListenersToDocument(document, true);
        return function () { return _this.unsubscribe(element, handler); };
    };
    /**
     * Unsubscribes an element from a particular handler.
     * Also removes the global listener from the document if there are no subscriptions left.
     */
    GlobalPointerDownOutsideListener.unsubscribe = function (elementToRemove, handler) {
        var currentSubscriptions = this.subscriptionMap.get(handler);
        if (currentSubscriptions && currentSubscriptions.indexOf(elementToRemove) !== -1) {
            currentSubscriptions = currentSubscriptions.filter(function (element) { return element !== elementToRemove; });
            this.subscriptionMap.set(handler, currentSubscriptions);
        }
        if (currentSubscriptions && currentSubscriptions.length === 0) {
            this.subscriptionMap.delete(handler);
        }
        // if there are no more subscriptions anywhere, remove the global listener
        if (this.subscriptionMap.size === 0) {
            this.addOrRemoveEventListenersToDocument(document, false);
        }
    };
    GlobalPointerDownOutsideListener.addOrRemoveEventListenersToDocument = function (doc, subscribe) {
        var _this = this;
        try {
            if (subscribe) {
                doc.addEventListener('mousedown', this.handlePointerDown);
                doc.addEventListener('touchstart', this.handlePointerDown);
            }
            else {
                doc.removeEventListener('mousedown', this.handlePointerDown);
                doc.removeEventListener('touchstart', this.handlePointerDown);
            }
            var iframes = doc.getElementsByTagName('iframe');
            [].forEach.call(iframes, function (iframe) {
                _this.addOrRemoveEventListenersToDocument(iframe.contentDocument, subscribe);
            });
        }
        catch (e) {
            /*
             * If the document is an iframe from a different source we can't add event listeners.
             * IE, Firefox, and Chrome throw different exceptions, so this just assumes
             * that is the error being thrown.
             */
        }
    };
    GlobalPointerDownOutsideListener.handlePointerDown = function (e) {
        var target = e.target;
        GlobalPointerDownOutsideListener.subscriptionMap.forEach(function (domNodes, handler) {
            var subscribersContainTarget = false;
            domNodes.some(function (domNode, index) {
                if (domNode && ariaContains(domNode, target)) {
                    subscribersContainTarget = true;
                    return true;
                }
                // If the target is not contained by any of the handler's subscribers, trigger that handler
                if (index === domNodes.length - 1 && !subscribersContainTarget) {
                    handler(e);
                }
                return false;
            });
        });
    };
    GlobalPointerDownOutsideListener.subscriptionMap = new Map();
    return GlobalPointerDownOutsideListener;
}());
exports.GlobalPointerDownOutsideListener = GlobalPointerDownOutsideListener;
// We check for any "virtually" descendant DOM referenced via `aria-controls` or `aria-owns` so that sub-popups work correctly
// TFSID 1156753
function ariaContains(element, target) {
    if (element.contains(target)) {
        return true;
    }
    var ariaDescendants = [];
    if (element.getAttribute('aria-controls') || element.getAttribute('aria-owns')) {
        ariaDescendants.push(element);
    }
    Array.from(element.querySelectorAll('[aria-controls], [aria-owns]')).forEach(function (node) { return ariaDescendants.push(node); });
    return ariaDescendants.some(function (elementWhichControls) {
        var controlledElement = document.getElementById(elementWhichControls.getAttribute('aria-controls'));
        var ownedElement = document.getElementById(elementWhichControls.getAttribute('aria-owns'));
        if (controlledElement || ownedElement) {
            return [controlledElement, ownedElement].some(function (elementToCheck) { return elementToCheck && ariaContains(elementToCheck, target); });
        }
        return false;
    });
}
//# sourceMappingURL=GlobalPointerDownOutsideListener.js.map